import { useState } from "react";
import { ChevronDown, ChevronRight, FileText } from "lucide-react";
import type { DocumentFragmentNode } from "@/api/knowledge";

interface Props {
  fragments: DocumentFragmentNode[];
}

const TYPE_LABELS: Record<string, string> = {
  BAB: "Bab",
  PASAL: "Pasal",
  AYAT: "Ayat",
  HURUF: "Huruf",
  BAGIAN: "Bagian",
  PARAGRAF: "Paragraf",
};

const TYPE_COLORS: Record<string, string> = {
  BAB: "bg-indigo-100 text-indigo-700",
  PASAL: "bg-emerald-100 text-emerald-700",
  AYAT: "bg-sky-100 text-sky-700",
  HURUF: "bg-amber-100 text-amber-700",
  BAGIAN: "bg-purple-100 text-purple-700",
  PARAGRAF: "bg-gray-100 text-gray-700",
};

export default function FragmentTree({ fragments }: Props) {
  if (!fragments || fragments.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
        <FileText className="h-6 w-6 mx-auto mb-2 text-gray-300" />
        <p className="text-sm text-gray-500">
          Belum ada fragmen terstruktur untuk dokumen ini.
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Fragmen otomatis ter-ekstrak saat pipeline AI mendeteksi struktur
          BAB/Pasal/Ayat dari teks.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      {fragments.map((f) => (
        <FragmentNode key={f.id} node={f} depth={0} />
      ))}
    </div>
  );
}

function FragmentNode({ node, depth }: { node: DocumentFragmentNode; depth: number }) {
  const [open, setOpen] = useState(depth < 1);
  const hasChildren = node.children && node.children.length > 0;
  const typeLabel = TYPE_LABELS[node.fragment_type] || node.fragment_type;
  const colorClass = TYPE_COLORS[node.fragment_type] || "bg-gray-100 text-gray-700";

  return (
    <div className="text-sm">
      <div
        className="flex items-start gap-2 py-1.5 hover:bg-gray-50 rounded px-1"
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-0.5 flex-shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          disabled={!hasChildren}
        >
          {hasChildren ? (
            open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <span className="inline-block h-3.5 w-3.5" />
          )}
        </button>
        <span className={`flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-mono ${colorClass}`}>
          {typeLabel}
          {node.nomor ? ` ${node.nomor}` : ""}
        </span>
        <div className="flex-1 min-w-0">
          {node.judul && (
            <p className="font-medium text-gray-800 break-words">{node.judul}</p>
          )}
          {node.teks && open && (
            <p className="mt-1 text-xs text-gray-600 whitespace-pre-wrap break-words">
              {node.teks}
            </p>
          )}
          {(node.page_start || node.page_end) && (
            <p className="mt-0.5 text-xs text-gray-400">
              hal. {node.page_start ?? "?"}
              {node.page_end && node.page_end !== node.page_start ? `–${node.page_end}` : ""}
            </p>
          )}
        </div>
      </div>
      {open && hasChildren && (
        <div>
          {node.children.map((c) => (
            <FragmentNode key={c.id} node={c} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
